import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Check, Crown, Sparkles, Gift } from "lucide-react";

const TIER_META = [
  { icon: Gift, accent: "from-zinc-500/30 to-zinc-500/0", id: "access-free" },
  { icon: Sparkles, accent: "from-bicenter-cyan/40 to-bicenter-blue/0", id: "access-premium" },
  { icon: Crown, accent: "from-bicenter-violet/50 to-bicenter-magenta/0", id: "access-vip" },
];

export const AccessLevels = () => {
  const { t } = useLanguage();

  return (
    <section id="access" className="relative py-28 md:py-36" data-testid="access-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-xs uppercase tracking-[0.4em] text-bicenter-cyan font-mono">{t.access.kicker}</p>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]">
            {t.access.title}
          </h2>
          <p className="mt-6 text-lg text-zinc-400 font-body leading-relaxed">{t.access.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {t.access.tiers.map((tier, i) => {
            const meta = TIER_META[i];
            const Icon = meta.icon;
            const isVIP = i === 2;
            const wrapperClass = isVIP
              ? "border-gradient-vip"
              : tier.highlighted
              ? "border-gradient-primary"
              : "";

            return (
              <div
                key={tier.name}
                data-testid={meta.id}
                className={`relative ${wrapperClass} ${!wrapperClass ? "border border-white/10 rounded-3xl" : ""} ${tier.highlighted ? "lg:scale-[1.04]" : ""}`}
              >
                <div className={`bg-[#0c0c0e] rounded-3xl p-8 h-full flex flex-col`}>
                  {tier.highlighted && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] bg-gradient-to-r from-bicenter-cyan to-bicenter-blue text-white px-3 py-1 rounded-full">
                      Recommended
                    </span>
                  )}
                  {isVIP && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] bg-gradient-to-r from-bicenter-violet to-bicenter-magenta text-white px-3 py-1 rounded-full">
                      Cercle privé
                    </span>
                  )}

                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${meta.accent} border border-white/10 flex items-center justify-center`}>
                    <Icon size={20} className="text-white" />
                  </div>

                  <h3 className="mt-6 font-display text-3xl text-white font-medium">{tier.name}</h3>
                  <p className="text-zinc-500 text-sm mt-1">{tier.tagline}</p>

                  <ul className="mt-8 space-y-3 flex-1">
                    {tier.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm text-zinc-300">
                        <Check size={14} className={isVIP ? "text-bicenter-magenta mt-0.5" : "text-bicenter-cyan mt-0.5"} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#join"
                    data-testid={`${meta.id}-cta`}
                    className={`mt-8 text-center text-sm font-medium py-3 px-5 rounded-full transition-all ${
                      isVIP
                        ? "bg-gradient-to-r from-bicenter-violet to-bicenter-magenta text-white hover:shadow-[0_0_30px_rgba(231,0,255,0.45)]"
                        : tier.highlighted
                        ? "bg-gradient-to-r from-bicenter-cyan to-bicenter-blue text-white hover:shadow-[0_0_30px_rgba(0,169,255,0.45)]"
                        : "border border-white/15 text-white hover:bg-white/5"
                    }`}
                  >
                    {t.access.cta}
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
